import { computed, ref } from 'vue';
import { apolloClient } from '@/plugins/graphql';
import gql from 'graphql-tag';

interface AccessTokenClaims {
    sub?: string;
    'cognito:username'?: string;
    'cognito:groups'?: string[];
    [key: string]: unknown;
}

export type PermissionType =
    | 'RfcSubmission'
    | 'RfcOwnOrganizationPrioritization'
    | 'RfcGlobalPrioritization'
    | 'RfcCancelling'
    | 'RfcViewAll'
    | 'AssetCreation'
    | 'AssetAndSensorTypeCreation'
    | 'AssetMonitoring'
    | 'AssetTasking'
    | 'AssetMaintenancePeriods'
    | 'ContactViewing'
    | 'ChartViewing'
    | 'CollectionPlanCreation'
    | 'NaiCreation'
    | 'AirControlMeasureCreationAndEditing'
    | 'SeaSurfaceControlMeasureCreationAndEditing'
    | 'SeaSubsurfaceControlMeasureCreationAndEditing'
    | 'GroupCreationAndPermissionsEditing'
    | 'Administrator';

const claims = ref<AccessTokenClaims | null>(decodeFromStorage());
const permissions = ref<PermissionType[]>([]);
const groupIds = ref<string[]>([]);
const permissionsLoaded = ref(false);

const ME_QUERY = gql`
    query Me {
        me {
            username
            permissions
            isCognitoAdmin
            groupIds
        }
    }
`;

function decodeFromStorage(): AccessTokenClaims | null {
    const token = localStorage.getItem('access-token');
    if (!token) return null;
    return decodeJwt(token);
}

function decodeJwt(token: string): AccessTokenClaims | null {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    try {
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '==='.slice((payload.length + 3) % 4);
        return JSON.parse(atob(padded)) as AccessTokenClaims;
    } catch {
        return null;
    }
}

async function loadPermissions(): Promise<void> {
    try {
        const { data } = await apolloClient.query<{
            me: {
                username: string | null;
                permissions: PermissionType[];
                isCognitoAdmin: boolean;
                groupIds: string[];
            };
        }>({
            query: ME_QUERY,
            fetchPolicy: 'no-cache',
        });
        permissions.value = data?.me?.permissions ?? [];
        groupIds.value = data?.me?.groupIds ?? [];
    } catch (err) {
        console.warn('[useAuth] failed to load me.permissions:', err);
        permissions.value = [];
        groupIds.value = [];
    } finally {
        permissionsLoaded.value = true;
    }
}

export function useAuth() {
    const refresh = () => {
        claims.value = decodeFromStorage();
    };

    const isSignedIn = computed(() => claims.value !== null);
    const sub = computed(() => claims.value?.sub ?? null);
    const username = computed(
        () => claims.value?.['cognito:username'] ?? localStorage.getItem('email') ?? null,
    );
    const email = computed(() => localStorage.getItem('email') || null);
    const groups = computed<string[]>(() => claims.value?.['cognito:groups'] ?? []);
    const isAdmin = computed(() => groups.value.includes('Admin'));

    const hasPermission = (p: PermissionType): boolean =>
        isAdmin.value
        || permissions.value.includes('Administrator')
        || permissions.value.includes(p);

    const canControlAsset = (controllerGroupIds: string[] | null | undefined): boolean => {
        if (isAdmin.value) return true;
        if (hasPermission('Administrator')) return true;
        if (hasPermission('AssetTasking')) return true;
        if (!controllerGroupIds || controllerGroupIds.length === 0) return false;
        return controllerGroupIds.some(gid => groupIds.value.includes(gid));
    };

    const ensurePermissionsLoaded = async () => {
        if (!permissionsLoaded.value && isSignedIn.value) await loadPermissions();
    };

    return {
        isSignedIn,
        sub,
        username,
        email,
        groups,
        isAdmin,
        permissions,
        groupIds,
        hasPermission,
        canControlAsset,
        refresh,
        loadPermissions,
        ensurePermissionsLoaded,
    };
}
